import { useEffect, useRef } from "react";
import { useForm, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { useJointBillErrorsStore } from "@store/jointBillErrors/jointBillErrors";
import { type IBillData } from "@services/overview/types";
import { isNullOrUndefined } from "@utils/common";
import {
  formatDecimalNumberInputValue,
  parseDecimalNumberInputValue,
} from "@utils/units";

interface IBillDetailValueFormProps {
  billDetailId?: number;
  name?: string;
  value: number;
  label: string;
  isEditable?: boolean;
  onSubmit?: (value: number) => Promise<IBillData>;
  isLoading?: boolean;
  isFixedNumber?: boolean;
}

interface IFormData {
  value: string;
}

export const BillDetailValueForm: React.FC<IBillDetailValueFormProps> = ({
  billDetailId,
  name,
  value,
  label,
  isEditable,
  onSubmit,
  isLoading,
  isFixedNumber = false,
}) => {
  const { t } = useTranslation();
  const { handleSubmit, control, setValue, setError } = useForm<IFormData>({
    defaultValues: {
      value: formatDecimalNumberInputValue(value, { isFixed: isFixedNumber }),
    },
  });
  const { errors: formErrors } = useFormState({ control });
  const isFormError = !!formErrors.value;
  const formRef = useRef<HTMLFormElement>(null);
  const { meterReadingErrors, clearMeterReadingErrors } =
    useJointBillErrorsStore();

  const reformatField = (value: string) => {
    setValue(
      "value",
      formatDecimalNumberInputValue(parseDecimalNumberInputValue(value), {
        isFixed: isFixedNumber,
      }),
    );
  };

  const submitForm = async (data: IFormData) => {
    reformatField(data.value);
    clearMeterReadingErrors();
    const parsedValue = parseDecimalNumberInputValue(data.value);
    const response = await onSubmit?.(parsedValue).catch(() => {
      toast.error(t("billDetail.toast.error"));
      setValue(
        "value",
        formatDecimalNumberInputValue(value, { isFixed: isFixedNumber }),
      );
    });

    if (!isNullOrUndefined(response)) {
      toast.success(t("billDetail.toast.success"));
    }
  };

  useEffect(() => {
    setValue(
      "value",
      formatDecimalNumberInputValue(value, { isFixed: isFixedNumber }),
    );
  }, [isFixedNumber, setValue, value]);

  useEffect(() => {
    if (!billDetailId || !name) {
      return;
    }

    const error = meterReadingErrors.find(
      (field) =>
        field.billDetailId === billDetailId && field.fieldName === name,
    );

    if (error) {
      setError("value", {
        message: error.text,
      });
    }
  }, [meterReadingErrors, billDetailId, name, setError]);

  return (
    <form
      ref={formRef}
      id={isFormError ? "InvalidBillForm" : undefined}
      onSubmit={handleSubmit(submitForm)}
    >
      <FormInput
        name="value"
        label={label}
        control={control}
        onBlur={() => {
          formRef.current?.dispatchEvent(
            new Event("submit", { cancelable: true, bubbles: true }),
          );
        }}
        onFocus={() => {
          if (value === 0) {
            setValue("value", "");
          }
        }}
        inputMode="decimal"
        disabled={!isEditable}
        isLoading={isLoading}
      />
    </form>
  );
};
